import { useMemo } from "react";
import { useUserQmProfile } from "@coherehealth/qm-api";
import useUserRoles from "./useUserRoles";

/**
 * LaunchDarkly evaluation context for the `qmFindACase` flag.
 *
 * Derived from the CURRENT USER (via `GET /user/myProfile` + `useUserRoles`), never from returned
 * cases — the Find a case page starts empty, so the Queues/Dashboard pattern of deriving LD context
 * from the health plans of returned cases cannot work here.
 *
 * Shape: `{ user: { key, opsGroup, roles } }` — a single `user`-kind context whose attributes carry
 * role and opsGroup. Only defined attributes are included.
 *
 * ⚠️ The shape is NESTED under `user` on purpose. core-platform's `createMultiContextFromMap`
 * (`LaunchDarklyService`) treats each TOP-LEVEL key of the forwarded map as a LaunchDarkly context
 * KIND, not an attribute. A flat `{ kind: "user", key, opsGroup, roles }` would therefore evaluate as
 * a four-kind multi-context (kinds literally `kind`/`key`/`opsGroup`/`roles`) with no `user` context
 * at all, so a rule targeting the `user` attribute `roles` would never fire. Nesting produces one
 * `user` context with `roles`/`opsGroup` as attributes, which is what the `qmFindACase` targeting
 * rules key on. The qm-service backend path (RQM-572) must build this identical nested shape.
 *
 * ⚠️ Known gap: `healthPlans[]` and `delegatedVendor` are omitted. There is no user-level frontend
 * source for them today (they are not on the Okta claims, the qm `/user/myProfile` User record, or the
 * Queue — which only carries `delegation: COHERE|GEISINGER`). Contrary to an earlier assumption, the
 * server does NOT enrich a caller-supplied `user` context: `getEvaluationWithContext` only appends the
 * Okta-derived user context when the caller sends a NON-user context, so anything omitted here is
 * simply absent at evaluation time. Day-1 targeting is role/opsGroup-only, so this is not a blocker;
 * a health-plan-scoped rule needs a follow-up that exposes a user-level health-plan source on the FE.
 */
export interface FindACaseLdUserAttributes {
  key?: string;
  opsGroup?: string;
  roles?: string[];
}

export interface FindACaseLdContext {
  // Index signature so this satisfies the LD hook's `context: { [key: string]: unknown }` param
  // while keeping the `user` context documented below.
  [key: string]: unknown;
  user: FindACaseLdUserAttributes;
}

export function useFindACaseLdContext(): FindACaseLdContext | undefined {
  // Base URL is supplied by QmRestfulProvider (config.QM_SERVICE_API_URL), so no explicit base here.
  const { data: profile } = useUserQmProfile({});
  // Fine-grained Okta roles, with envRestricted_<env>_ prefixes stripped (per useUserRoles).
  const roles = useUserRoles();

  const key = profile?.username;
  const opsGroup = profile?.opsGroup;
  const rolesKey = roles?.join(",");

  return useMemo<FindACaseLdContext | undefined>(() => {
    // Wait until we have at least an identifier or roles before evaluating, so we don't cache a
    // premature empty context (the LD hook cache-keys on JSON.stringify(context)).
    if (!key && !opsGroup && !rolesKey) {
      return undefined;
    }
    const user: FindACaseLdUserAttributes = {};
    if (key) {
      user.key = key;
    }
    if (opsGroup) {
      user.opsGroup = opsGroup;
    }
    if (roles && roles.length > 0) {
      user.roles = roles;
    }
    return { user };
    // rolesKey is the stable string form of roles; roles identity may change each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, opsGroup, rolesKey]);
}
